import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/* Minimal session hook for Intraop.
 *
 * Returns the current Supabase session (or null if not signed in) plus
 * helpers to sign out and to build a sign-in URL that bounces back
 * here after a successful login on plan.cadtomie.com.
 *
 * We deliberately don't replicate the planner's full `useAuth` hook —
 * Intraop doesn't sign users up, doesn't reset passwords, doesn't
 * resend verification emails. All of that happens on the planner.
 * Intraop just *reads* the session and provides a sign-out and a way
 * to start the SSO flow. */

export interface SessionState {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (cancelled) return;
        setSession(newSession);
      },
    );
    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    loading,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };
}

/** Build the URL that opens the planner's login page and brings the
 *  user back to Intraop after a successful sign-in. */
export function buildSignInUrl(returnTo: string = window.location.href): string {
  const planner = resolvePlannerOrigin();
  return `${planner}/login?return=${encodeURIComponent(returnTo)}`;
}

/** Detect whether we're on prod or dev and return the matching planner
 *  origin. Falls back to plan.cadtomie.com so a misconfigured dev
 *  environment still sends users to a known-good sign-in. */
function resolvePlannerOrigin(): string {
  if (typeof window === 'undefined') return 'https://plan.cadtomie.com';
  const { hostname, protocol } = window.location;
  // Capacitor's WKWebView serves the bundled app from `capacitor://localhost`.
  // The `localhost` hostname here is the in-process scheme, NOT a Mac dev
  // server, so the localhost branch below would route to a non-existent
  // http://localhost:5173. Always route Capacitor builds to the prod planner.
  // (The proper App-Store-safe flow — SFSafariViewController + custom URL
  // scheme that returns INTO the app — is implemented separately in
  // `@capacitor/browser` integration, see Step 7 of the iOS adaptation.)
  if (protocol === 'capacitor:') return 'https://plan.cadtomie.com';
  if (hostname.endsWith('.cadtomie.com')) return 'https://plan.cadtomie.com';
  if (hostname.endsWith('.cadtomie.local')) return `${protocol}//plan.cadtomie.local:5173`;
  if (hostname === 'localhost') return 'http://localhost:5173';
  return 'https://plan.cadtomie.com';
}
